<template>
  <v-card v-if="!loader.loading" class="mb-6 pa-4" title="Resumo por categoria">
    <v-table>
      <tbody>
        <tr>
          <th class="font-weight-bold">
            Categoria
          </th>
          <th class="font-weight-bold text-no-wrap">
            Quantidade <span class="opacity-50" style="font-size: 10px;">(%)</span>
          </th>
          <th class="font-weight-bold text-end">
            Valor Bruto
          </th>
          <th class="font-weight-bold text-end">
            Descontos
          </th>
          <th v-if="can('read', 'fees')" class="font-weight-bold text-end">
            Taxa de Serviço
          </th>
          <th class="font-weight-bold text-end">
            Receita
          </th>
        </tr>
        <tr v-for="(label, key) in categoryLabels" :key="key">
          <td>{{ label }}</td>
          <template v-if="categories[key]">
            <td>
              {{ categories[key].quantity }}
              <span class="opacity-50" style="font-size: 10px;">({{ Math.round(categories[key].quantity / categoriesCount * 100) }}%)</span>
            </td>
            <td class="text-end">
              {{ currency(categories[key].gross_revenue) }}
            </td>
            <td class="text-end">
              {{ currency(categories[key].discounts) }}
            </td>
            <td v-if="can('read', 'fees')" class="text-end">
              {{ currency(categories[key].fees) }}
            </td>
            <td class="text-end">
              {{ currency(categories[key].revenue) }}
            </td>
          </template>
          <template v-else>
            <td>
              0 <span class="opacity-50" style="font-size: 10px;">(0%)</span>
            </td>
            <td class="text-end">
              {{ currency(0) }}
            </td>
            <td class="text-end">
              {{ currency(0) }}
            </td>
            <td v-if="can('read', 'fees')" class="text-end">
              {{ currency(0) }}
            </td>
            <td class="text-end">
              {{ currency(0) }}
            </td>
          </template>
        </tr>
      </tbody>
    </v-table>
  </v-card>

  <v-card v-if="!loader.loading" class="mb-6 pa-4" title="Resumo por forma de pagamento">
    <v-table>
      <tbody>
        <tr>
          <th class="font-weight-bold">
            Forma de Pagamento
          </th>
          <th class="font-weight-bold">
            Parcelamento
          </th>
          <th class="font-weight-bold text-no-wrap">
            Quantidade <span class="opacity-50" style="font-size: 10px;">(%)</span>
          </th>
          <th class="font-weight-bold text-end">
            Valor Bruto
          </th>
          <th class="font-weight-bold text-end">
            Descontos
          </th>
          <th v-if="can('read', 'fees')" class="font-weight-bold text-end">
            Taxa de Serviço
          </th>
          <th class="font-weight-bold text-end">
            Receita
          </th>
        </tr>
        <tr v-for="item, key in paymentChoices" :key="key">
          <td>{{ item.label }}</td>
          <td>{{ item.installments }}</td>
          <td>
            {{ item.quantity }}
            <span class="opacity-50" style="font-size: 10px;">({{ Math.round(item.quantity / paymentChoicesCount * 100) }}%)</span>
          </td>
          <td class="text-end">
            {{ currency(item.gross_revenue) }}
          </td>
          <td class="text-end">
            {{ currency(item.discounts) }}
          </td>
          <td v-if="can('read', 'fees')" class="text-end">
            {{ currency(item.fees) }}
          </td>
          <td class="text-end">
            {{ currency(item.revenue) }}
          </td>
        </tr>
      </tbody>
    </v-table>
  </v-card>

  <v-card v-if="!loader.loading" class="mb-6 pa-4" title="Resumo por setor">
    <v-table>
      <tbody>
        <tr>
          <th class="font-weight-bold">
            Setor
          </th>
          <th class="font-weight-bold">
            Emitidos
          </th>
          <th class="font-weight-bold text-no-wrap">
            Computados
          </th>
          <th class="font-weight-bold text-end">
            Arrecadação
          </th>
        </tr>
        <tr v-for="(records, id) in groupBy<RevenueRecord, string>(items, item => item.sector)" :key="id">
          <td>{{ records[0].sector }}</td>
          <td>{{ records.reduce((acc, curr) => acc + curr.count, 0) }}</td>
          <td>{{ records.reduce((acc, curr) => acc + curr.read, 0) }}</td>
          <td class="text-end">
            {{ currency(records.reduce((acc, curr) => acc + curr.revenue, 0) - records.reduce((acc, curr) => acc + curr.discounts, 0)) }}
          </td>
        </tr>
      </tbody>
    </v-table>
  </v-card>
</template>

<script lang="ts" setup>
import { useLoader } from '@/stores/loader'
import RevenueRecord from '@/types/RevenueRecord'
import { computed } from 'vue'
import { currency } from '@/helpers/formatters/currency'
import { useAbility } from '@casl/vue'
import { groupBy } from '@/helpers/array'

const loader = useLoader()
const { can } = useAbility()

const { items } = defineProps<{ items: RevenueRecord[] }>()

// --- Category Table Items ---
type Category = 'paid' | 'free' | 'complimentary'

const categoryLabels: Record<Category, string> = {
  paid: 'Ingressos com valor',
  complimentary: 'Cortesias',
  free: 'Gratuidades',
}

const categories = computed(() => {
  return buildTableItems(items, (item) => {
    let key: Category = 'paid'
    if (item.type === 'Gratuidade') key = 'free'
    if (item.type === 'Cortesia') key = 'complimentary'

    return {
      key,
      overrides: { label: categoryLabels[key] },
    }
  })
})

const categoriesCount = computed(() => Object.values(categories.value).reduce((acc, curr) => acc + curr.quantity, 0))

// --- Payment Choice Table Items ---
const paymentChoices = computed(() => {
  return buildTableItems(
    items.filter(item => !['Gratuidade', 'Cortesia'].includes(item.type)),
    (item) => {
      return {
        key: `${item.payment_method}${item.installments}`,
        overrides: { label: item.payment_method, installments: item.installments.toString() },
      }
    },
  )
})

const paymentChoicesCount = computed(() => Object.values(paymentChoices.value).reduce((acc, curr) => acc + curr.quantity, 0))

// --- Table Item Builder ---
interface TableItem {
  label: string
  installments?: string
  quantity: number
  gross_revenue: number
  discounts: number
  fees: number
  revenue: number
}

type BuilderCallback = (a: RevenueRecord) => { key: string, overrides: Partial<TableItem> }

function buildTableItems(items: RevenueRecord[], criteria: BuilderCallback) {
  return items.reduce((acc, curr) => {
    const { key, overrides } = criteria(curr)

    const entry = acc[key] ?? getEmptyTableItem(overrides)

    entry.quantity += curr.count
    entry.gross_revenue += curr.revenue
    entry.discounts += curr.discounts
    entry.fees += curr.fees
    entry.revenue += curr.revenue - curr.discounts

    return { ...acc, [key]: entry }
  }, {} as Record<ReturnType<BuilderCallback>['key'], TableItem>)
}

function getEmptyTableItem(overrides?: Partial<TableItem>): TableItem {
  return {
    label: '',
    quantity: 0,
    gross_revenue: 0,
    discounts: 0,
    fees: 0,
    revenue: 0,
    ...overrides,
  }
}
</script>
